package org.vreo.devicehub.features.device.presentation.deviceList.components

import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.TimeZone

import androidx.compose.runtime.Composable
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.presentation.getFormatedDate
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.ProductType

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.id_title
import devicehub.composeapp.generated.resources.device_card_software_version_title
import devicehub.composeapp.generated.resources.device_card_last_active_title
import devicehub.composeapp.generated.resources.device_card_tag_title

private val activeColor = Color(0xFF5AAA55)
private val inactiveColor = Color(0xFF575456)

/**
 * Device item composable. presents [AnprDevice] object.
 *
 * @param device device to present
 * @param onClick click handler
 * @param modifier composable modifier
 * @param openItemClick on open menu item click handler, if set to null item will be disabled
 * @param unlinkItemClick on unlike menu item click handler, if set to null item will be disabled
 * @param deleteItemClick on delete menu item click handler, if set to null item will be disabled
 */
@Composable
fun DeviceItem(
    device: AnprDevice,
    onClick: (AnprDevice) -> Unit,
    modifier: Modifier = Modifier,
    openItemClick: ((device: AnprDevice) -> Unit)? = null,
    unlinkItemClick: ((device: AnprDevice) -> Unit)? = null,
    deleteItemClick: ((device: AnprDevice) -> Unit)? = null,
) {
    Card(
        modifier = modifier
            .clickable(onClick = { onClick(device) }),
        colors = if (device.active) {
            CardDefaults.cardColors(
                containerColor = activeColor,
                contentColor = Color.Black
            )
        } else {
            CardDefaults.cardColors(
                containerColor = inactiveColor,
                contentColor = Color.White
            )
        }
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier
                .padding(8.dp)
                .fillMaxWidth()
                .height(IntrinsicSize.Max),
        ) {
            Column(
                verticalArrangement = Arrangement.Center,
            ) {
                Text(
                    text = "${stringResource(Res.string.id_title)} ${device.deviceId}",
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                Text(
                    text = "${stringResource(Res.string.device_card_tag_title)} ${device.userTag}",
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "${stringResource(Res.string.device_card_software_version_title)} ${device.softwareVersion}",
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                Text(
                    text = "${stringResource(Res.string.device_card_last_active_title)} ${
                        if (device.lastActivityTime != null) {
                            getFormatedDate(device.lastActivityTime)
                        } else {
                            "N/A"
                        }
                    }",
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            DeviceDropDownMenu(
                openItemClick = if (openItemClick != null) {
                    { openItemClick(device) }
                } else null,
                unlinkItemClick = if (unlinkItemClick != null) {
                    { unlinkItemClick(device) }
                } else null,
                deleteItemClick = if (deleteItemClick != null) {
                    { deleteItemClick(device) }
                } else null
            )
        }
    }
}

@OptIn(ExperimentalTime::class)
@Preview
@Composable
fun DeviceItemPreview() {
    val instant = Instant.fromEpochMilliseconds(1760360075238)
    val timeAndDate = instant.toLocalDateTime(TimeZone.currentSystemDefault())

    Column {
        DeviceItem(
            device = AnprDevice(
                deviceId = "volt-s1-0001",
                device = ProductType.VoltS1PoleShortRange,
                serialNumber = "0001",
                softwareVersion = "1.2.3",
                ocrVersion = "1.2.3",
                localIpAddress = "192.168.1.1",
                macAddress = "111:222:333",
                active = true,
                lastActivityTime = timeAndDate,
            ),
            onClick = { },
        )
        DeviceItem(
            device = AnprDevice(
                deviceId = "volt-s1-0001",
                device = ProductType.VoltS1PoleShortRange,
                serialNumber = "0001",
                softwareVersion = "1.2.3",
                ocrVersion = "1.2.3",
                localIpAddress = "192.168.1.1",
                macAddress = "111:222:333",
                active = false,
                lastActivityTime = timeAndDate,
            ),
            onClick = { },
        )
    }
}